"use client";

import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { AnimatePresence } from "framer-motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { DemoSuccessDialog } from "@/components/contact/DemoSuccessDialog";
import { Check } from "@/components/ui/icons";
import { Select, type SelectGroup } from "@/components/ui/Select";
import { CONTACT, DEMO_SUCCESS, PRODUCTS, type Product } from "@/content/quikit";
import { cn } from "@/lib/utils";
import { SectionIntro } from "./SectionIntro";
import { LEAD_ENDPOINT as ENDPOINT } from "@/lib/lead-endpoint";

const SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];

/**
 * Every app, grouped by family. PRODUCTS is ordered with each family contiguous, so folding
 * adjacent entries keeps the picker in the same order as the nav without a second list.
 */
const APP_GROUPS = PRODUCTS.reduce<{ family: string; apps: Product[] }[]>((acc, p) => {
  const last = acc[acc.length - 1];
  if (last?.family === p.family) last.apps.push(p);
  else acc.push({ family: p.family, apps: [p] });
  return acc;
}, []);

/**
 * `min`/`max` are the digit count of the national number, which is what makes a typed number
 * checkable rather than merely non-empty. India and the US lead — that is where the offices are.
 */
type Dial = { iso: string; country: string; code: string; min: number; max: number };

const DIAL_CODES: Dial[] = [
  { iso: "IN", country: "India", code: "+91", min: 10, max: 10 },
  { iso: "US", country: "United States", code: "+1", min: 10, max: 10 },
  { iso: "AE", country: "United Arab Emirates", code: "+971", min: 8, max: 9 },
  { iso: "AU", country: "Australia", code: "+61", min: 9, max: 9 },
  { iso: "BD", country: "Bangladesh", code: "+880", min: 10, max: 10 },
  { iso: "BR", country: "Brazil", code: "+55", min: 10, max: 11 },
  { iso: "CA", country: "Canada", code: "+1", min: 10, max: 10 },
  { iso: "CH", country: "Switzerland", code: "+41", min: 9, max: 9 },
  { iso: "CN", country: "China", code: "+86", min: 11, max: 11 },
  { iso: "DE", country: "Germany", code: "+49", min: 10, max: 11 },
  { iso: "ES", country: "Spain", code: "+34", min: 9, max: 9 },
  { iso: "FR", country: "France", code: "+33", min: 9, max: 9 },
  { iso: "GB", country: "United Kingdom", code: "+44", min: 9, max: 10 },
  { iso: "ID", country: "Indonesia", code: "+62", min: 9, max: 12 },
  { iso: "IE", country: "Ireland", code: "+353", min: 7, max: 9 },
  { iso: "IL", country: "Israel", code: "+972", min: 8, max: 9 },
  { iso: "IT", country: "Italy", code: "+39", min: 9, max: 10 },
  { iso: "JP", country: "Japan", code: "+81", min: 10, max: 10 },
  { iso: "KE", country: "Kenya", code: "+254", min: 9, max: 9 },
  { iso: "KR", country: "South Korea", code: "+82", min: 9, max: 10 },
  { iso: "LK", country: "Sri Lanka", code: "+94", min: 9, max: 9 },
  { iso: "MX", country: "Mexico", code: "+52", min: 10, max: 10 },
  { iso: "MY", country: "Malaysia", code: "+60", min: 9, max: 10 },
  { iso: "NG", country: "Nigeria", code: "+234", min: 10, max: 10 },
  { iso: "NL", country: "Netherlands", code: "+31", min: 9, max: 9 },
  { iso: "NZ", country: "New Zealand", code: "+64", min: 8, max: 10 },
  { iso: "PH", country: "Philippines", code: "+63", min: 10, max: 10 },
  { iso: "PK", country: "Pakistan", code: "+92", min: 10, max: 10 },
  { iso: "PL", country: "Poland", code: "+48", min: 9, max: 9 },
  { iso: "QA", country: "Qatar", code: "+974", min: 8, max: 8 },
  { iso: "SA", country: "Saudi Arabia", code: "+966", min: 9, max: 9 },
  { iso: "SE", country: "Sweden", code: "+46", min: 7, max: 9 },
  { iso: "SG", country: "Singapore", code: "+65", min: 8, max: 8 },
  { iso: "TH", country: "Thailand", code: "+66", min: 9, max: 9 },
  { iso: "TR", country: "Turkey", code: "+90", min: 10, max: 10 },
  { iso: "VN", country: "Vietnam", code: "+84", min: 9, max: 10 },
  { iso: "ZA", country: "South Africa", code: "+27", min: 9, max: 9 },
];

const dialOf = (iso: string) => DIAL_CODES.find((d) => d.iso === iso) ?? DIAL_CODES[0];

/**
 * Self-hosted from `public/flags` (41KB for the set). Flag emoji were the obvious route but
 * Windows ships no flag glyphs, so Chrome there renders the country letters instead.
 */
function Flag({ iso }: { iso: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={`/flags/${iso.toLowerCase()}.png`}
      alt=""
      width={20}
      height={14}
      loading="lazy"
      className="h-[14px] w-5 shrink-0 rounded-[2px] object-cover ring-1 ring-inset ring-ink/[0.08]"
    />
  );
}

const DIAL_GROUPS: SelectGroup[] = [
  {
    options: DIAL_CODES.map((d) => ({
      value: d.iso,
      label: d.country,
      note: d.code,
      icon: <Flag iso={d.iso} />,
      keywords: `${d.code} ${d.iso}`,
    })),
  },
];

const SIZE_GROUPS: SelectGroup[] = [{ options: SIZES.map((s) => ({ value: `${s} employees`, label: `${s} employees` })) }];

const PRODUCT_GROUPS: SelectGroup[] = [
  { options: [{ value: "", label: "The whole suite" }] },
  ...APP_GROUPS.map((g) => ({
    label: g.family,
    options: g.apps.map((p) => ({ value: p.name, label: p.name, note: p.status === "soon" ? "coming soon" : undefined })),
  })),
];

/** Throwaway inbox providers — a submission from one of these is not a real lead. */
const DISPOSABLE_DOMAINS = new Set([
  "10minutemail.com", "discard.email", "dispostable.com", "emailondeck.com", "fakeinbox.com",
  "getnada.com", "guerrillamail.com", "inboxkitten.com", "luxusmail.org", "maildrop.cc",
  "mailinator.com", "mailnesia.com", "minuteinbox.com", "moakt.com", "mohmal.com",
  "mytemp.email", "sharklasers.com", "spamgourmet.com", "temp-mail.org", "tempmail.com",
  "tempr.email", "throwawaymail.com", "tmpmail.org", "trashmail.com", "yopmail.com",
]);

/** Personal mailbox providers. The field asks for a work address, so these are turned away. */
const PERSONAL_DOMAINS = new Set([
  "126.com", "163.com", "aol.com", "gmail.com", "googlemail.com", "gmx.com", "hotmail.co.uk",
  "hotmail.com", "icloud.com", "live.com", "mac.com", "mail.com", "mail.ru", "me.com",
  "msn.com", "outlook.com", "pm.me", "proton.me", "protonmail.com", "qq.com",
  "rediffmail.com", "rocketmail.com", "tutanota.com", "yahoo.co.in", "yahoo.co.uk",
  "yahoo.com", "yandex.com", "ymail.com",
]);

/** 1234567890 and 0987654321 pass a length check but are never real numbers. */
const isRun = (s: string) =>
  [...s].every((c, i, a) => i === 0 || +c === (+a[i - 1] + 1) % 10) ||
  [...s].every((c, i, a) => i === 0 || +c === (+a[i - 1] + 9) % 10);

type Errors = Partial<Record<"name" | "email" | "phone" | "company" | "size", string>>;

function validate(d: Record<string, string>): Errors {
  const e: Errors = {};

  if (!d.name?.trim()) e.name = "Please enter your name.";

  const email = (d.email ?? "").trim().toLowerCase();
  const domain = email.split("@")[1] ?? "";
  if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/.test(email)) e.email = "Please enter a valid email address.";
  else if (DISPOSABLE_DOMAINS.has(domain)) e.email = "That is a temporary inbox. Please use your work email.";
  else if (PERSONAL_DOMAINS.has(domain)) e.email = "Please use your work email rather than a personal one.";

  const dial = dialOf(d.country ?? "IN");
  const digits = (d.phone ?? "").replace(/\D/g, "");
  if (!digits) e.phone = "Please enter your contact number.";
  else if (digits.length < dial.min || digits.length > dial.max)
    // Phrased to avoid an article — "a India number" / "in United Kingdom" both mis-read.
    e.phone =
      dial.min === dial.max
        ? `Enter ${dial.min} digits for ${dial.country}.`
        : `Enter ${dial.min}–${dial.max} digits for ${dial.country}.`;
  else if (/^(\d)\1+$/.test(digits) || isRun(digits)) e.phone = "Please enter a real contact number.";
  else if (dial.iso === "IN" && !/^[6-9]/.test(digits)) e.phone = "An Indian mobile number starts with 6, 7, 8 or 9.";

  if (!d.company?.trim()) e.company = "Please enter your company.";
  if (!d.size) e.size = "Please choose a company size.";

  return e;
}

/**
 * Submissions go to this site's own endpoint, which stores the lead and emails it. The
 * shared secret the receiving script can require now lives on the server with it, instead of
 * shipping in the public bundle. Set NEXT_PUBLIC_FORM_DISABLED=1 for preview mode: the form
 * validates and shows the confirmation but stores nothing.
 */

type Status = "idle" | "sending" | "error";

const field =
  "h-11 w-full rounded-[12px] bg-white px-3.5 text-[14px] outline-none ring-1 ring-inset ring-ink/[0.08] transition-shadow placeholder:text-faint focus:ring-2 focus:ring-accent aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[#c2413b]";

function Field({
  id,
  label,
  error,
  optional,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label id={`${id}-label`} htmlFor={id} className="mb-1.5 block text-[12px] font-medium text-ink/70">
        {label} {optional && <span className="font-normal text-faint">(optional)</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-err`} role="alert" className="mt-1 text-[11.5px] text-[#c2413b]">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Book a demo — the contact form plus Quikit's phone and offices.
 *
 * Used both as a homepage section and as the whole of `/contact`. `className` replaces the
 * section's own spacing, which is the only thing that differs between the two: in the page
 * flow it needs section rhythm above it, standing alone it needs to clear the fixed navbar.
 */
export function Contact({
  className,
  /** `h2` as a homepage section; `h1` when the /contact route makes it the whole page. */
  as = "h2",
}: { className?: string; as?: "h1" | "h2" } = {}) {
  const [status, setStatus] = useState<Status>("idle");
  /** The sender's name once the request is in; `null` while the form is still showing. */
  const [done, setDone] = useState<string | null>(null);
  const returnUrl = useRef("");
  const [errors, setErrors] = useState<Errors>({});
  const [iso, setIso] = useState("IN");
  const [size, setSize] = useState("");
  const [product, setProduct] = useState("");
  const dial = dialOf(iso);

  /** Clear a field's error as soon as it is edited, so the form stops nagging. */
  const clear = (k: keyof Errors) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;

    const errs = validate(data);
    setErrors(errs);
    if (Object.keys(errs).length) {
      form.querySelector<HTMLElement>(`[name="${Object.keys(errs)[0]}"]`)?.focus();
      return;
    }

    const picked = dialOf(data.country);
    const digits = data.phone.replace(/\D/g, "");
    setStatus("sending");
    try {
      if (ENDPOINT) {
        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            topic: "demo",
            country: picked.country,
            countryIso: picked.iso,
            countryCode: picked.code,
            phone: digits,
            phoneE164: `${picked.code}${digits}`,
            page: typeof window === "undefined" ? "" : window.location.pathname,
          }),
        });
        // The endpoint reports why it failed; a bare status check would hide a lost lead.
        const reply = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !reply?.ok) throw new Error(reply?.error ?? String(res.status));
      } else {
        await new Promise((r) => setTimeout(r, 900));
      }
      openSuccess(data.name ?? "");
      form.reset();
      setStatus("idle");
      setIso("IN");
      setSize("");
      setProduct("");
    } catch {
      setStatus("error");
    }
  }

  /**
   * Show the confirmation and move the address bar to /form-submitted without navigating,
   * so the dialog stays over the page. That route exists for real, so a reload or a shared
   * link lands on the same confirmation instead of a dead URL.
   */
  function openSuccess(name: string) {
    returnUrl.current = window.location.pathname + window.location.search + window.location.hash;
    if (window.location.pathname !== DEMO_SUCCESS.url) {
      window.history.pushState({ quikitDemoSent: true }, "", DEMO_SUCCESS.url);
    }
    setDone(name);
  }

  const closeSuccess = useCallback(() => {
    setDone(null);
    if (window.location.pathname === DEMO_SUCCESS.url && returnUrl.current) {
      window.history.replaceState({}, "", returnUrl.current);
    }
  }, []);

  // Back should dismiss the confirmation, not jump past the page sitting behind it.
  useEffect(() => {
    if (done === null) return;
    const onPop = () => setDone(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [done]);

  return (
    // The form card is transformed by its reveal, which traps the dropdown panel in the card's
    // stacking context; the footer (also transformed) then paints over any panel that overhangs
    // the card. Lifting the whole section keeps an open dropdown on top. Still below the navbar.
    <section id="contact" aria-labelledby="contact-title" className={cn("relative z-20", className ?? "pt-[clamp(120px,12vw,190px)]")}>
      <div className="wrap grid gap-10 md:grid-cols-[0.9fr_1.1fr] md:gap-14">
        <div>
          <SectionIntro
            as={as}
            align="left"
            size="split"
            eyebrow="Book a demo"
            title="See Quikit running on your business."
            titleId="contact-title"
            text="Tell us what you’re solving. We’ll walk you through the apps that fit — and how they connect."
            titleClassName="max-w-[11em]"
          />
          {/* What they are booking a demo of — the column ran empty under the offices. */}
          <ul className="mt-8 space-y-3">
            {CONTACT.points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-[14px] leading-[1.55]">
                <span className="mt-[3px] grid size-[18px] shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                  <Check className="size-[11px]" strokeWidth={2.6} />
                </span>
                {p}
              </li>
            ))}
          </ul>

          <dl className="mt-9 space-y-6 text-[14px]">
            <div>
              <dt className="text-muted">Call us</dt>
              <dd className="mt-1">
                <a href={CONTACT.phone.href} className="text-[15px] font-medium hover:text-accent">
                  {CONTACT.phone.label}
                </a>
              </dd>
            </div>
          </dl>
          {/* Its own list: dt/dd may sit in a div directly inside a dl, not two levels down. mt-6
              keeps the 24px the shared list's space-y-6 gave it. */}
          <dl className="mt-6 grid grid-cols-2 gap-6 text-[14px]">
            {CONTACT.offices.map((o) => (
              <div key={o.region}>
                <dt className="text-muted">{o.region}</dt>
                <dd className="mt-1 leading-[1.6]">
                  {o.lines.map((l) => (
                    <span key={l} className="block">
                      {l}
                    </span>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <AnimatedCard
          y={70}
          className="rounded-[28px] bg-panel p-5 shadow-[0_1px_2px_rgba(10,10,11,0.04),0_30px_60px_-32px_rgba(10,10,11,0.28)] md:p-8"
        >
          <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2" noValidate>
              <Field id="c-name" label="Full name" error={errors.name}>
                <input
                  id="c-name"
                  name="name"
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "c-name-err" : undefined}
                  onChange={() => clear("name")}
                  className={field}
                  placeholder="Jane Doe"
                />
              </Field>

              <Field id="c-email" label="Work email" error={errors.email}>
                <input
                  id="c-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "c-email-err" : undefined}
                  onChange={() => clear("email")}
                  className={field}
                  placeholder="jane@company.com"
                />
              </Field>

              <Field id="c-phone" label="Contact number" error={errors.phone} className="sm:col-span-2">
                <div className="flex gap-2">
                  <Select
                    id="c-dial"
                    name="country"
                    ariaLabel="Country dialling code"
                    value={iso}
                    onChange={(v) => {
                      setIso(v);
                      clear("phone");
                    }}
                    groups={DIAL_GROUPS}
                    searchable
                    searchPlaceholder="Search country or code"
                    className="w-[104px] shrink-0"
                    panelClassName="w-[272px]"
                    // The trigger has room for the flag and the code only; the name is in the list.
                    renderValue={(o) => (
                      <>
                        {o.icon}
                        <span className="truncate">{o.note}</span>
                      </>
                    )}
                  />
                  <input
                    id="c-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    maxLength={dial.max + 6}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "c-phone-err" : undefined}
                    onChange={() => clear("phone")}
                    className={field}
                    placeholder={dial.min === dial.max ? `${dial.min} digits` : `${dial.min}–${dial.max} digits`}
                  />
                </div>
              </Field>

              <Field id="c-company" label="Company" error={errors.company}>
                <input
                  id="c-company"
                  name="company"
                  autoComplete="organization"
                  aria-invalid={!!errors.company}
                  aria-describedby={errors.company ? "c-company-err" : undefined}
                  onChange={() => clear("company")}
                  className={field}
                  placeholder="Company name"
                />
              </Field>

              <Field id="c-size" label="Company size" error={errors.size}>
                <Select
                  id="c-size"
                  name="size"
                  value={size}
                  onChange={(v) => {
                    setSize(v);
                    clear("size");
                  }}
                  groups={SIZE_GROUPS}
                  placeholder="Select size"
                  ariaLabelledby="c-size-label"
                  ariaDescribedby={errors.size ? "c-size-err" : undefined}
                  invalid={!!errors.size}
                />
              </Field>

              <Field id="c-app" label="Most interested in" className="sm:col-span-2">
                <Select
                  id="c-app"
                  name="product"
                  value={product}
                  onChange={setProduct}
                  groups={PRODUCT_GROUPS}
                  placeholder="The whole suite"
                  ariaLabelledby="c-app-label"
                  searchable
                  searchPlaceholder="Search apps"
                />
              </Field>

              <Field id="c-msg" label="What are you trying to solve?" optional className="sm:col-span-2">
                <textarea
                  id="c-msg"
                  name="message"
                  rows={4}
                  className={cn(field, "h-auto resize-none py-3")}
                  placeholder="A few words about your team and tools today"
                />
              </Field>

              <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
                <p className="text-[11.5px] text-muted">
                  {status === "error" ? "Something went wrong — please try again or call us." : "We reply within one business day."}
                </p>
                <button type="submit" disabled={status === "sending"} className="btn btn-mint h-11 px-6 text-[14px] disabled:opacity-60">
                  <span className="btn-label" data-btn-label="">
                    <span data-text={status === "sending" ? "Sending…" : "Request a demo"}>{status === "sending" ? "Sending…" : "Request a demo"}</span>
                  </span>
                </button>
              </div>
          </form>
        </AnimatedCard>
      </div>

      <AnimatePresence>{done !== null && <DemoSuccessDialog name={done} onClose={closeSuccess} />}</AnimatePresence>
    </section>
  );
}
